import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import { NotFoundError } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';

import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import {
  ServiceCategoryService,
  ToastMessageService,
} from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';

import { combineLatest, Observable } from 'rxjs';

import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  AbstractControl,
} from '@angular/forms';
import { MembershipBehaviour, Status } from '@fitness-force/enum';
import { SelectModule } from 'primeng/select';
import type {
  WaiverMasterResponse,
  WaiverMasterResponseList,
} from '@fitness-force/models';

@Component({
  selector: 'service-category-create',
  templateUrl: './service-category-create.component.html',

  providers: [ToastMessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    RippleModule,
    DialogModule,
    DividerModule,
    InputTextModule,
    TranslateModule,
    ErrorMsgComponent,
    SelectModule,
  ],
})
export class ServiceCategoryCreateComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly serviceCategoryService = inject(ServiceCategoryService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly activeCategory = input<any>();
  localActiveCategory: any = null;
  readonly onlyOne = input(false);
  readonly defaultBehaviour = input<any>(null);
  readonly changed = output();
  readonly close = output();

  getAccessTenantList$: Observable<any> = new Observable();
  showOptions = false;
  tenantId = 0;
  membershipBehaviour: any[] = [];

  serverCategoryForm: UntypedFormGroup;
  primaryLanguage!: string;
  secondaryLanguage!: string;
  isSecondaryLanguage = false;
  apptCatParams = new Map();
  isSubmitting = false;
  waivermasters: WaiverMasterResponse[] = [];

  setBehaviour() {
    for (let [key, value] of Object.entries(MembershipBehaviour)) {
      this.translate
        .get('ADMIN.SUBSCRIPTION.' + key.toString().toUpperCase())
        .subscribe((translations) => {
          this.membershipBehaviour.push({ name: translations, key: key });
        });
    }
  }

  ngOnInit(): void {
    this.setBehaviour();
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);

    combineLatest([
      this.getAccessTenantList$,
      this.store.select(COMPANY_INFO),
    ]).subscribe({
      next: ([tenant, company]: any) => {
        this.tenantId = tenant.selected_tenant.id;
        if (this.tenantId > 0) {
          this.store.dispatch({ type: 'ROUTER_GO' });

          if (tenant.selected_tenant?.secondary_language) {
            this.isSecondaryLanguage = true;
            this.translate
              .get([
                'common.LANGUAGE.' +
                  tenant.selected_tenant.primary_language?.toUpperCase(),
                'common.LANGUAGE.' +
                  tenant.selected_tenant.secondary_language?.toUpperCase(),
              ])
              .subscribe((translations) => {
                this.primaryLanguage =
                  translations[
                    'common.LANGUAGE.' +
                      tenant.selected_tenant.primary_language?.toUpperCase()
                  ];
                this.secondaryLanguage =
                  translations[
                    'common.LANGUAGE.' +
                      tenant.selected_tenant.secondary_language?.toUpperCase()
                  ];
              });
          }
          this.loadWaiverMasters();
        }
      },
    });
  }

  handleError(error: any) {
    if (error instanceof NotFoundError) {
      this.translate
        .get('common.errors.SERVER_SIDE.PAGE_NOT_FOUND')
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    } else if (error.originalError?.error?.[0]) {
      const currentError = error.originalError?.error?.[0];
      if (currentError.errorCode == 'alreadyexist') {
        if (
          currentError?.identifier &&
          currentError.identifier == 'name_pri_lang'
        )
          this.NamePrimary?.setErrors({
            duplicate: true,
          });
        else if (
          currentError?.identifier &&
          currentError.identifier == 'name_sec_lang'
        )
          this.NameSecondary?.setErrors({
            duplicate: true,
          });
        this.changeDetectorRef.detectChanges();
      }
    }
    {
      this.translate
        .get('common.errors.SERVER_SIDE.SOMETHING_WRONG')
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    }
  }

  loadWaiverMasters() {
    this.serviceCategoryService.getWaiverMasters(this.tenantId).subscribe({
      next: (res: WaiverMasterResponseList) => {
        this.waivermasters = res.items
          ? res.items.filter(
              (obj) =>
                obj.status == Status.Active ||
                obj.status?.toString() == Status[Status.Active],
            )
          : [];
        this.loadCategory();
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.loadCategory();
        this.handleError(error);
      },
    });
  }

  loadCategory() {
    const activeCategory = this.activeCategory();
    if (activeCategory) {
      this.serviceCategoryService
        .getSingleServiceCategory(this.tenantId, activeCategory.id)
        .subscribe({
          next: (res) => {
            this.localActiveCategory = res;
            this.buildEditForm();
            this.changeDetectorRef.detectChanges();
          },
          error: (error) => {
            this.handleError(error);
          },
        });
    } else {
      this.buildEditForm();
    }
  }

  get NamePrimary(): AbstractControl | null {
    return this.serverCategoryForm.get('name_primary_language');
  }
  get NameSecondary(): AbstractControl | null {
    return this.serverCategoryForm.get('name_secondary_language');
  }

  get DescriptionPrimary(): AbstractControl | null {
    return this.serverCategoryForm.get('description_primary_language');
  }

  get DescriptionSecondary(): AbstractControl | null {
    return this.serverCategoryForm.get('description_secondary_language');
  }
  get Behavior(): AbstractControl | null {
    return this.serverCategoryForm.get('behaviour');
  }

  get SelectedWaiver(): AbstractControl | null {
    return this.serverCategoryForm.get('waiver_master_id');
  }

  buildEditForm() {
    const activeCategory = this.activeCategory();
    const activeCategoryValue = this.activeCategory();
    const activeCategoryVal = this.activeCategory();
    const activeCategoryInput = this.activeCategory();
    const activeCategory_1 = this.activeCategory();
    const activeCategory_2 = this.activeCategory();
    const activeCategory_3 = this.activeCategory();
    const activeCategory_4 = this.activeCategory();
    this.serverCategoryForm = this.fb.group({
      name_primary_language: [
        activeCategory?.name_primary_language
          ? activeCategory.name_primary_language
          : '',
        Validators.required,
      ],
      name_secondary_language: [
        activeCategoryValue?.name_secondary_language
          ? activeCategoryValue.name_secondary_language
          : '',
      ],
      description_primary_language: [
        activeCategoryVal?.description_primary_language
          ? activeCategoryVal.description_primary_language
          : '',
      ],
      description_secondary_language: [
        activeCategoryInput &&
        activeCategoryInput.description_secondary_language
          ? activeCategoryInput.description_secondary_language
          : '',
        ``,
      ],
      behaviour: [
        {
          value: this.initializeSubscriptionCategoryForm(),
          disabled: this.onlyOne(),
        },
      ],
      waiver_master_id: [
        activeCategory_1 &&
        activeCategory_1.waiver_master_id &&
        activeCategory_1.waiver_master_id > 0
          ? this.waivermasters.find(
              (obj) => obj.id == this.activeCategory().waiver_master_id,
            )
          : '',
      ],
      terms_condition: [
        activeCategory_2 && activeCategory_2.terms_condition
          ? activeCategory_2.terms_condition
          : '',
      ],
      terms_condition_title: [
        activeCategory_3 && activeCategory_3.terms_condition_title
          ? activeCategory_3.terms_condition_title
          : '',
      ],
      icon: [
        activeCategory_4 && activeCategory_4.icon ? activeCategory_4.icon : '',
      ],
    });
  }

  initializeSubscriptionCategoryForm() {
    const defaultBehaviour = this.defaultBehaviour();
    const activeCategory = this.activeCategory();
    if (activeCategory?.behaviour) {
      for (const [key, value] of Object.entries(MembershipBehaviour)) {
        if (key == activeCategory.behaviour) {
          return { name: value, key };
        }
      }
      return {
        name: MembershipBehaviour.MemberShip,
        key: 'Membership',
      };
    } else if (this.onlyOne() && defaultBehaviour) {
      for (const [key, value] of Object.entries(MembershipBehaviour)) {
        if (key == defaultBehaviour) {
          return { name: value, key };
        }
      }
    }
    return {
      name: MembershipBehaviour.MemberShip,
      key: 'Membership',
    };
  }

  get Icon(): AbstractControl | null {
    return this.serverCategoryForm.get('icon');
  }
  setIcon(icon: string) {
    this.Icon?.setValue(icon);
  }

  updateServiceCategory() {
    if (this.serverCategoryForm.valid) {
      const data = this.serverCategoryForm.getRawValue();
      // get value of behaviour ..
      data.behaviour = data.behaviour.key;
      data.waiver_master_id = data.waiver_master_id
        ? data.waiver_master_id.id
        : 0;
      const activeCategory = this.activeCategory();
      if (activeCategory) {
        this.isSubmitting = true;
        this.serviceCategoryService
          .updateServiceCategory(this.tenantId, activeCategory.id, data)
          .subscribe({
            next: (res) => {
              this.isSubmitting = false;
              this.changed.emit(res);
              this.translate
                .get('ADMIN.SERVICES.SERVICE_CATEGORY_UPDATE_MESSAGE')
                .subscribe((translations) => {
                  this.toastMessageService.showSuccess(translations);
                });
            },
            error: (error) => {
              this.isSubmitting = false;
              this.handleError(error);
            },
          });
      } else {
        this.isSubmitting = true;
        this.serviceCategoryService
          .createServiceCategory(this.tenantId, data)
          .subscribe({
            next: (res) => {
              this.isSubmitting = false;
              this.localActiveCategory = null;
              this.changed.emit(res);
              this.translate
                .get('ADMIN.SERVICES.SERVICE_CATEGORY_CREATED_SUCCESSFULLY')
                .subscribe((translations) => {
                  this.toastMessageService.showSuccess(translations);
                });
            },
            error: (error) => {
              this.isSubmitting = false;
              this.handleError(error);
            },
          });
      }
    } else {
      Object.keys(this.serverCategoryForm.controls).forEach((field) => {
        const control = this.serverCategoryForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  categoryCancel() {
    this.changed.emit();
  }
  closeDialog() {
    this.close.emit();
  }
}
